<template>
  <Transition name="fade">
    <div
      v-if="visible"
      class="fixed inset-0 z-50 flex items-center justify-center"
      style="background: rgba(10, 10, 18, 0.6); backdrop-filter: blur(4px)"
      :aria-busy="true"
      :aria-hidden="false"
      @keydown.esc.prevent.stop="escToClose && handleClose()"
    >
      <button
        v-if="closeOnBackdrop"
        class="absolute inset-0 cursor-default"
        @click="handleClose"
        aria-label="Close loading overlay"
        tabindex="-1"
      />

      <Transition name="pop">
        <div
          class="relative z-50 w-[90%] max-w-sm"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title ? ids.title : undefined"
          :aria-describedby="ids.desc"
        >
          <div
            class="rounded-2xl border border-white/10 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 p-8"
          >
            <!-- Indeterminate (spinner) -->
            <div v-if="!isDeterminate" class="flex flex-col items-center">
              <svg
                width="52"
                height="52"
                viewBox="0 0 52 52"
                class="mb-5"
                role="status"
                aria-live="polite"
              >
                <circle
                  cx="26"
                  cy="26"
                  r="21"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  class="opacity-10"
                />
                <circle
                  cx="26"
                  cy="26"
                  r="21"
                  fill="none"
                  stroke="#7F77DD"
                  stroke-width="3"
                  stroke-linecap="round"
                  class="spinner-arc"
                />
              </svg>

              <p
                v-if="title"
                :id="ids.title"
                class="text-base font-medium text-center leading-snug"
              >
                {{ title }}
              </p>
              <p
                v-if="subtitle"
                class="text-sm text-neutral-500 dark:text-neutral-400 mt-1.5 text-center leading-relaxed"
              >
                {{ subtitle }}
              </p>
              <p :id="ids.desc" class="sr-only">Loading in progress</p>
            </div>

            <!-- Determinate (progress bar) -->
            <div v-else class="flex flex-col">
              <div class="flex items-baseline justify-between mb-1.5">
                <p
                  v-if="title"
                  :id="ids.title"
                  class="text-base font-medium leading-snug"
                >
                  {{ title }}
                </p>
                <span
                  v-if="showPercent"
                  class="text-sm font-medium tabular-nums"
                  style="color: #7f77dd"
                >
                  {{ clampedProgress }}%
                </span>
              </div>
              <p
                v-if="subtitle"
                class="text-sm text-neutral-500 dark:text-neutral-400 mb-4 leading-relaxed"
              >
                {{ subtitle }}
              </p>

              <div
                class="w-full h-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden mt-2"
              >
                <div
                  class="h-full rounded-full progress-bar"
                  :style="{ width: clampedProgress + '%' }"
                  aria-hidden="true"
                />
              </div>

              <p :id="ids.desc" class="sr-only">
                Progress {{ clampedProgress }} percent
              </p>
            </div>

            <!-- Actions -->
            <div
              v-if="cancelable || dismissible"
              class="mt-6 pt-5 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-center gap-2"
            >
              <button
                v-if="cancelable"
                @click="$emit('cancel')"
                class="px-4 py-2 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              >
                Cancel
              </button>
              <button
                v-if="dismissible"
                @click="handleClose"
                class="px-4 py-2 text-sm rounded-lg font-medium text-white transition-opacity hover:opacity-90"
                style="background: #7f77dd"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";

const props = defineProps({
  visible: { type: Boolean, required: true },
  progress: { type: [Number, null], default: null },
  title: { type: String, default: "" },
  subtitle: { type: String, default: "" },
  showPercent: { type: Boolean, default: true },
  dismissible: { type: Boolean, default: false },
  cancelable: { type: Boolean, default: false },
  escToClose: { type: Boolean, default: false },
  closeOnBackdrop: { type: Boolean, default: false },
});

const emit = defineEmits(["close", "cancel"]);

const ids = {
  title: `loader-title-${Math.random().toString(36).slice(2)}`,
  desc: `loader-desc-${Math.random().toString(36).slice(2)}`,
};

const isNumber = (v) => typeof v === "number" && !Number.isNaN(v);
const isDeterminate = computed(() => isNumber(props.progress));
const clampedProgress = computed(() => {
  if (!isNumber(props.progress)) return 0;
  return Math.min(100, Math.max(0, Math.round(props.progress)));
});

function handleClose() {
  if (props.dismissible) emit("close");
}
</script>

<style scoped>
/* Backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 200ms ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Dialog pop */
.pop-enter-active,
.pop-leave-active {
  transition: transform 200ms ease, opacity 200ms ease;
}
.pop-enter-from,
.pop-leave-to {
  transform: translateY(8px) scale(0.97);
  opacity: 0;
}

/* Spinner arc */
@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}
.spinner-arc {
  animation: dash 1.4s ease-in-out infinite;
}

/* Progress bar */
.progress-bar {
  background: linear-gradient(90deg, #afa9ec, #7f77dd);
  transition: width 300ms ease;
}
</style>
